import React, { useEffect, useState, useRef } from "react";
import { ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { FacilityIcon, FACILITY_ICON_NAMES } from "../../lib/facilityIcons";

export const POS_OPTS = ["center", "top", "bottom", "left", "right", "left top", "right top", "left bottom", "right bottom"];

export const STATUSES = ["pending", "pending_payment", "pending_verification", "confirmed", "checked_in", "checked_out", "completed", "cancelled", "refunded"];

export const STATUS_COLOR = {
  pending: "bg-stone-100 text-stone-600", pending_payment: "bg-amber-100 text-amber-700",
  pending_verification: "bg-orange-100 text-orange-700",
  confirmed: "bg-green-100 text-green-700", checked_in: "bg-blue-100 text-blue-700",
  checked_out: "bg-indigo-100 text-indigo-700", completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700", refunded: "bg-purple-100 text-purple-700",
};
export const VERIF_LABEL = { requested: "Verification Pending", pending: "Verification Submitted", approved: "Verified", rejected: "Verification Rejected", overridden: "Overridden by Admin", none: "No ID" };
export const VERIF_COLOR = { requested: "bg-amber-100 text-amber-700", pending: "bg-blue-100 text-blue-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700", overridden: "bg-purple-100 text-purple-700", none: "bg-stone-100 text-stone-500" };

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function IconPicker({ value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const filtered = FACILITY_ICON_NAMES.filter((n) => n.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="relative shrink-0" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testid} className="h-9 w-9 rounded-lg bg-rose-50 flex items-center justify-center hover:bg-rose-100 transition-colors">
        <FacilityIcon name={value} size={18} className="text-rose" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-56 rounded-lg border border-stone-200 bg-white shadow-xl p-2">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search icons…" data-testid={`${testid}-search`} className="w-full rounded border border-stone-200 px-2 py-1.5 text-xs mb-2" />
          <div className="grid grid-cols-6 gap-1 max-h-44 overflow-y-auto">
            {filtered.map((n) => (
              <button key={n} type="button" title={n} onClick={() => { onChange(n); setOpen(false); setQ(""); }} className={`h-8 w-8 flex items-center justify-center rounded hover:bg-sand ${value === n ? "bg-rose/15" : ""}`}>
                <FacilityIcon name={n} size={16} className="text-forest" />
              </button>
            ))}
            {filtered.length === 0 && <span className="col-span-6 text-[11px] text-stone-400 py-2 text-center">No icons match</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="card-lux p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide text-stone-500">{label}</span>
        <Icon size={18} className="text-rose" />
      </div>
      <div className="font-serif text-3xl text-forest mt-2">{value}</div>
      {hint && <div className="text-xs text-stone-400 mt-1">{hint}</div>}
    </div>
  );
}

const parsePos = (v) => {
  const m = (v || "").match(/(-?\d+(?:\.\d+)?)%\s+(-?\d+(?:\.\d+)?)%/);
  return m ? { x: +m[1], y: +m[2] } : { x: 50, y: 50 };
};

export function FocalAdjuster({ url, value, zoom: zoomProp, onChange, onClose, frameClass = "aspect-square" }) {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(() => parsePos(value));
  const [zoom, setZoom] = useState(() => zoomProp || 1);
  const move = (clientX, clientY) => {
    const r = ref.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, Math.round(((clientX - r.left) / r.width) * 100)));
    const y = Math.max(0, Math.min(100, Math.round(((clientY - r.top) / r.height) * 100)));
    setPos({ x, y });
  };
  const down = (e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); move(e.clientX, e.clientY); };
  const drag = (e) => { if (dragging.current) move(e.clientX, e.clientY); };
  const up = () => { dragging.current = false; };
  const posStr = `${pos.x}% ${pos.y}%`;
  const done = () => { onChange({ position: posStr, zoom }); onClose?.(); };
  const imgStyle = { objectPosition: posStr, transform: zoom !== 1 ? `scale(${zoom})` : undefined, transformOrigin: posStr };
  return (
    <div className="p-3 space-y-3" data-testid="focal-adjuster">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <p className="text-[11px] text-stone-500">Drag to reposition · slider to zoom.</p>
          <div
            ref={ref} onPointerDown={down} onPointerMove={drag} onPointerUp={up}
            className={`relative ${frameClass} w-full rounded-lg overflow-hidden cursor-crosshair select-none touch-none ring-2 ring-rose`}
          >
            <img src={url} alt="" draggable={false} className="h-full w-full object-cover pointer-events-none" style={imgStyle} />
            <div className="absolute h-6 w-6 -ml-3 -mt-3 rounded-full border-2 border-white bg-rose/60 shadow pointer-events-none" style={{ left: `${pos.x}%`, top: `${pos.y}%` }} />
          </div>
        </div>
        <div className="w-24 shrink-0 space-y-1">
          <p className="text-[10px] uppercase tracking-wide text-stone-400">Guests see</p>
          <div className={`relative ${frameClass} w-full rounded-lg overflow-hidden border border-stone-200`} data-testid="focal-preview">
            <img src={url} alt="" className="h-full w-full object-cover" style={imgStyle} />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <ZoomOut size={14} className="text-stone-400" />
        <input type="range" min="1" max="3" step="0.1" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="focal-zoom" className="flex-1 accent-rose" />
        <ZoomIn size={14} className="text-stone-400" />
        <span className="text-[11px] text-stone-500 w-8 text-right">{zoom.toFixed(1)}x</span>
      </div>
      <div className="flex gap-2">
        <button onClick={() => { setPos({ x: 50, y: 50 }); setZoom(1); }} data-testid="focal-reset" className="flex-1 flex items-center justify-center gap-1 text-xs rounded py-1.5 bg-stone-100 text-stone-600 hover:bg-stone-200"><RotateCcw size={12} /> Reset</button>
        <button onClick={done} data-testid="focal-done" className="flex-1 text-xs rounded py-1.5 bg-forest text-cream hover:bg-forest/90">Save crop</button>
      </div>
    </div>
  );
}

export function CropModal({ url, value, zoom, aspect = "aspect-square", onSave, onClose }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="crop-modal">
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <FocalAdjuster url={url} value={value} zoom={zoom} frameClass={aspect} onChange={onSave} onClose={onClose} />
      </div>
    </div>
  );
}
